// Create localized routes by prefixing paths with /ja or /en
// and making sure route names are unique across locales.
import { i18n } from '@/i18n-setup'

// NOTE:
// Do NOT JSON-clone route records. vite-plugin-pages sets `component` as a function
// (lazy loader). JSON cloning drops functions, resulting in routes without components
// and blank RouterView. We shallow-clone and recursively process children instead.
function cloneRoute(route) {
  const cloned = { ...route };
  if (Array.isArray(route.children)) {
    cloned.children = route.children.map((c) => cloneRoute(c));
  }
  return cloned;
}

function prefixPath(path, locale) {
  // normalize
  if (!path || path === '/') return `/${locale}`;
  if (path.startsWith('/')) return `/${locale}${path}`;
  return `/${locale}/${path}`;
}

function transformOne(route, locale) {
  const r = cloneRoute(route);
  // path
  r.path = prefixPath(r.path || '/', locale);
  // name: keep JA names as-is for backward compat; add suffix for EN
  if (r.name) {
    if (locale === 'en') r.name = `${String(r.name)}__en`;
  }
  // children recursive
  if (Array.isArray(r.children) && r.children.length) {
    r.children = r.children.map((c) => transformOne(c, locale));
  }
  return r;
}

export function addLocalePrefix(routes, locale) {
  return routes.map((r) => transformOne(r, locale));
}

export function buildLocalizedRoutes(rawRoutes) {
  const ja = addLocalePrefix(rawRoutes, 'ja');
  const en = addLocalePrefix(rawRoutes, 'en');
  return [...ja, ...en];
}

// ---- Fallback Redirects ----
// Generate non-localized routes that redirect to the current locale
// Example: '/subscription' -> `/${currentLocale}/subscription`

function joinPath(parent, child) {
  const p = parent || ''
  const c = child || ''
  if (!p && !c) return '/'
  if (c.startsWith('/')) return c
  if (!p || p === '/') return `/${c.replace(/^\//, '')}`
  return `${p.replace(/\/$/, '')}/${c.replace(/^\//, '')}`
}

function collectAbsolutePaths(routes, parentPath = '', acc = new Set()) {
  for (const r of routes) {
    const abs = joinPath(parentPath, r.path || '/')
    acc.add(abs)
    if (Array.isArray(r.children) && r.children.length) {
      collectAbsolutePaths(r.children, abs, acc)
    }
  }
  return acc
}

export function buildFallbackRedirects(rawRoutes) {
  const paths = Array.from(collectAbsolutePaths(rawRoutes))
    .filter((p) => p && p !== '/')

  return paths.map((p) => ({
    path: p,
    // Functional redirect keeps params/query/hash as-is
    redirect: (to) => {
      const current = (i18n && i18n.global && i18n.global.locale === 'en') ? 'en' : 'ja'
      return {
        path: `/${current}${to.path}`,
        query: to.query,
        hash: to.hash,
        replace: true,
      }
    },
  }))
}
